/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useCallback, useState, useMemo } from 'react';
import _ from "lodash";
import { Inbox } from '@jda/lui-common-icon-library-mui5';
import { useSelector, useDispatch } from 'react-redux';
import { LuiListCollector } from '@jda/lui-common-component-library-mui5';
import { Box, CircularProgress, ListItem, ListItemText, Typography, Backdrop, Alert } from '@mui/material';

import {
  ACCESS_ROLES,
  ASSIGN_ROLES,
  DELETE_ROLES,
  ASSIGNED_ROLES,
  AVAILABLE_ROLES,
  EMPTY_LIST_TITLE,
  ACCESS_SUBJECT_ROLES,
  ROLE_INFO_MESSAGE,
  EMPTY_LIST_SUB_TITLE,
} from './constants';
import Loader from '../Loader';
import { useStyles } from './styles';
import { getAllRoles } from '../../services/roles';
import { IRole } from '../../models/Roles/RolesContracts';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { compareByProperty } from '../../utility/commonUtility';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import interpolateString from '../../utility/stringInterpolation';
import { selectRoleOfSubject } from '../../store/role/roleSelector';
import { getRoleOfSubjectAction } from '../../store/role/roleActionCreator';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../container/Machines/constants';
import { selectShowHideRoleAssignmentLoader } from '../../store/loader/loaderSelector';
import { showHideRoleAssignmentLoaderAction } from '../../store/loader/loaderActionCreator';
import { assignRolesToGroupsRequest, deleteRolesToGroupsRequest, getRolesAssignedToGroups } from '../../services/userGroups';

interface IRoleAssignmentList {
  groupName: string;
  identifier: string;
}

const RoleAssignmentList = ({ groupName, identifier }: IRoleAssignmentList) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const selectedRole = useSelector(selectRoleOfSubject);
  const showLoader = useSelector(selectShowHideRoleAssignmentLoader);

  const [roleList, setRoleList] = useState<any>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalProgress, setTotalProgress] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [leftSelectedRoles, setLeftSelectedRoles] = useState<any>([]);
  const [assignedRolesList, setAssignedRolesList] = useState<any>([]);
  const [rightSelectedRoles, setRightSelectedRoles] = useState<any>([]);
  const [availableRolesList, setAvailableRolesList] = useState<any>([]);
  const [allowRenderingOfAssignedRoleList, setAllowRenderingOfAssignedRoleList] = useState(false);
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });

  const getRoles = useCallback(() => {
    dispatch(showHideRoleAssignmentLoaderAction(true));
    getAllRoles().then((response: IRole[]) => {
      const filteredResponse = response.filter(role => !role.hasAssignedLocations);
      setRoleList(filteredResponse.sort(compareByProperty()));
    }).catch(_error => {
      const errorMessage = {
        message: interpolateString([ACCESS_ROLES])
      };
      setErrors(errorMessage);
    }).finally(() => {
      dispatch(showHideRoleAssignmentLoaderAction(false));
    });
  }, [dispatch]);

  const getRolesOfSubject = useCallback((identifier: string) => {
    setAllowRenderingOfAssignedRoleList(false);
    dispatch(showHideRoleAssignmentLoaderAction(true));
    getRolesAssignedToGroups(identifier).then((response: any) => {
      if (response.data.length) {
        const sortedResponse = response.data.map((role: any) => {
          return {
            ...role,
            id: role.roleId,
            name: role.roleName,
          };
        });
        setAssignedRolesList(sortedResponse);
        setAllowRenderingOfAssignedRoleList(true);
        dispatch(getRoleOfSubjectAction(sortedResponse));
      } else {
        setAssignedRolesList([]);
        dispatch(getRoleOfSubjectAction([]));
      }
      dispatch(showHideRoleAssignmentLoaderAction(false));
    }).catch(_error => {
      if (_error.response && _error.response.status === 404) {
        dispatch(getRoleOfSubjectAction([]));
      } else {
        const errorMessage = {
          message: interpolateString([ACCESS_SUBJECT_ROLES])
        };
        setErrors(errorMessage);
      }
    }).finally(() => {
      dispatch(showHideRoleAssignmentLoaderAction(false));
    });
  }, [dispatch]);

  useEffect(() => {
    setErrors({ message: '' });
    getRoles();
    getRolesOfSubject(identifier);
  }, [getRoles, getRolesOfSubject, identifier]);

  useEffect(() => {
    const availableRoles = roleList.filter((role: any) => !(_.find(assignedRolesList, { id: role.roleId })));
    setAvailableRolesList(availableRoles);
  }, [roleList, assignedRolesList]);

  const assignRoles = async (newValue: any) => {
    return new Promise((resolve, reject) => {
      const newRoleToAssign = newValue;
      const ListOfRolesId = newValue.map((role: any) => role.id.toString());
      const roles = {
        "roleIds": ListOfRolesId
      };
      assignRolesToGroupsRequest(identifier, roles).then(_response => {
        dispatch(getRoleOfSubjectAction([...selectedRole, newRoleToAssign]));
        resolve(true);
      }).catch(_error => {
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: interpolateString([ASSIGN_ROLES]),
            severity: 'error'
          })
        );
        reject();
      });
    })
  };

  const deleteRole = async (rolesList: any) => {
    const ListOfRolesId = rolesList.map((role: any) => role.id.toString());
    const roles = {
      "roleIds": ListOfRolesId
    };
    return new Promise((resolve, reject) => {
      deleteRolesToGroupsRequest(identifier, roles).then(() => {
        dispatch(getRoleOfSubjectAction(selectedRole.filter((entry: IRole) => entry !== rolesList)));
        dispatch(showHideRoleAssignmentLoaderAction(false));
        resolve(true);
      }).catch(_error => {
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            message: interpolateString([DELETE_ROLES]),
            severity: 'error'
          })
        );
        reject();
      });
    });
  };

  const filterAssignedOrRemovedRoles = async (items: any) => {
    setCurrentProgress(0);
    const prevAssignedRolesList = [...assignedRolesList];
    const deletedRolesList: any[] = [];
    if (prevAssignedRolesList.length) {
      prevAssignedRolesList.forEach((role: any) => {
        if (!_.find(items, { id: role.id })) {
          deletedRolesList.push(role);
        }
      });
    };
    const addedRolesList = _.pullAllBy(items, prevAssignedRolesList, 'id');
    dispatch(showHideRoleAssignmentLoaderAction(true));
    if (allowRenderingOfAssignedRoleList && deletedRolesList.length) {
      setIsDeleting(true);
      setTotalProgress(deletedRolesList.length);
      await deleteRole(deletedRolesList);
      getRoles();
      getRolesOfSubject(identifier);
      setAllowRenderingOfAssignedRoleList(true);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: `${deletedRolesList.length} role${deletedRolesList.length > 1 ? 's are' : ' is'} deleted from ${groupName}`,
          severity: 'success'
        })
      );
    };
    if (addedRolesList.length) {
      setIsDeleting(false);
      setTotalProgress(addedRolesList.length);
      await assignRoles(addedRolesList);
      getRoles();
      getRolesOfSubject(identifier);
      setAllowRenderingOfAssignedRoleList(true);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: `${addedRolesList.length} role${addedRolesList.length > 1 ? 's are' : ' is'} assigned to ${groupName}`,
          severity: 'success'
        })
      );
    };
    setLeftSelectedRoles([]);
    setRightSelectedRoles([]);
    dispatch(showHideRoleAssignmentLoaderAction(false));
  };
  const onClickListItem = (roleItem: any, setSelected: any) => {
    const { data } = roleItem.item;
    if (_.find(availableRolesList, { id: data.id }) && rightSelectedRoles.length === 0) {
      if (leftSelectedRoles.length < 5) {
        if (_.find(leftSelectedRoles, { id: data.id })) {
          const filteredRoles = leftSelectedRoles.filter((item: any) => item.id !== data.id);
          setLeftSelectedRoles(filteredRoles);
        } else {
          setLeftSelectedRoles([...leftSelectedRoles, data]);
        }
        setSelected(roleItem.item);
      } else if (leftSelectedRoles.length === 5 && _.find(leftSelectedRoles, { id: data.id })) {
        const filteredRoles = leftSelectedRoles.filter((item: any) => item.id !== data.id);
        setLeftSelectedRoles(filteredRoles);
        setSelected(roleItem.item);
      }
    } else if (_.find(assignedRolesList, { id: data.id }) && leftSelectedRoles.length === 0) {
      if (rightSelectedRoles.length < 5) {
        if (_.find(rightSelectedRoles, { id: data.id })) {
          const filteredRoles = rightSelectedRoles.filter((item: any) => item.id !== data.id);
          setRightSelectedRoles(filteredRoles);
        } else {
          setRightSelectedRoles([...rightSelectedRoles, data]);
        }
        setSelected(roleItem.item);
      } else if (rightSelectedRoles.length === 5 && _.find(rightSelectedRoles, { id: data.id })) {
        const filteredRoles = rightSelectedRoles.filter((item: any) => item.id !== data.id);
        setRightSelectedRoles(filteredRoles);
        setSelected(roleItem.item);
      }
    }
  };

  const renderRoleListCollector = useMemo(() => {
    return (
      <>
        <LuiListCollector
          width={400}
          height={450}
          itemKey="id"
          allowClearAll
          divider={true}
          allowSearch={true}
          isDraggable={true}
          onLoadSubmit={false}
          listData={roleList}
          displayNameProperty="name"
          leftListLabel={AVAILABLE_ROLES}
          rightListLabel={ASSIGNED_ROLES}
          defaultSelectedItems={assignedRolesList}
          customMessages={{ clearAll: 'Clear Assigned Roles' }}
          leftListEmptyMessage={
            <Box className={classes.innerContent} id="empty-component">
              <Inbox fontSize="large" color="action" />
              <Box id="empty-component-text" className={classes.titleAlignment}>
                {EMPTY_LIST_TITLE}
              </Box>
            </Box>
          }
          rightListEmptyMessage={
            <Box className={classes.innerContent} id="empty-component">
              <Inbox fontSize="large" color="action" />
              <Box id="empty-component-text" className={classes.titleAlignment}>
                {EMPTY_LIST_TITLE}
              </Box>
              <Box id="empty-component-title">{EMPTY_LIST_SUB_TITLE}</Box>
            </Box>
          }
          customListItem={(roleItem: any, setSelected: any) => {
            const { data } = roleItem.item;
            return (
              <ListItem
                key={data.id}
                role="listitem"
                className={classes.roleItem}
                data-testid={`list-item-${data.id}`}
                onClick={() => onClickListItem(roleItem, setSelected)}
              >
                <ListItemText id={data.id} primary={data.name} />
              </ListItem>
            )
          }}
          disableDefaultOnClick={true}
          onSubmit={filterAssignedOrRemovedRoles}
        />
      </>
    )
  }, [roleList, assignedRolesList, leftSelectedRoles, rightSelectedRoles, availableRolesList]);

  return (
    <>
      {(
        <Backdrop open={!!(currentProgress && (currentProgress !== totalProgress))} >
          <Box className={classes.progressBarWrap}>
            <CircularProgress
              variant="determinate"
              className={classes.circlularLoader}
              aria-label={'circular-progress-determinate'}
              value={(currentProgress / totalProgress) * 100}
            />
            <Typography variant="h4" gutterBottom component="div">
              <Box fontWeight="fontWeightRegular">
                {currentProgress} Roles {isDeleting ? "Disassociated" : "Assigned"} from {totalProgress}
              </Box>
            </Typography>
          </Box>
        </Backdrop>
      )}
      <Box className={classes.warningInfoWrap}>
        <Alert
          elevation={0}
          severity={(leftSelectedRoles.length === 5 || rightSelectedRoles.length === 5) ? 'warning' : 'info'}
          variant={'outlined'}
        >
          {ROLE_INFO_MESSAGE}
        </Alert>
      </Box>
      <Box className={classes.root}>
        {renderRoleListCollector}
        {showLoader ? (
          <Loader showLoader={true} isCenter customClasses={classes.loader} />
        ) : errors ? (
          <ErrorMessage {...errors} />
        ) : null}
      </Box>
    </>
  );
};

export default RoleAssignmentList;
